// Voice engines → Test users (plan §6.5.3): find an account by email or uid
// (Firebase Auth), give it an engine and profile until an expiry, and see
// what each test user's next call gets now (the resolver's dry run).

import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteOverride,
  getOverrides,
  lookupUser,
  putOverride,
  type EngineInfo,
  type EngineName,
  type OverrideRow,
  type OverridesResponse,
  type Profile,
  type UserLookup,
} from "../../services/voiceEngines";
import { formatDateTime } from "../analytics/format";
import { Callout } from "./parts";
import { errorText, isForbidden } from "./errors";
import { clientText, decisionWhy, ENGINE_LABEL, ENGINE_ORDER, profileText } from "./labels";
import { EXPIRY_DAYS, expiresAtFor, expiryChoice, expiryText, parseWho, profileChoices, type ExpiryTone } from "./testUsers";

interface Props {
  profiles: Profile[];
  engines: EngineInfo[];
  canWrite: boolean;
  environment: string;
  onForbidden: (message: string) => void;
  onChanged: () => Promise<void>;
}

const TONE_CLASS: Record<ExpiryTone, string> = { expired: "ve-error", soon: "ve-warn", later: "", never: "" };

function expiryLabel(days: number | null): string {
  if (days === null) return "Never";
  return days === 1 ? "In 1 day" : `In ${days} days`;
}

export function TestUsersTab({ profiles, engines, canWrite, environment, onForbidden, onChanged }: Props) {
  const [data, setData] = useState<OverridesResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [who, setWho] = useState("");
  const [found, setFound] = useState<UserLookup | null>(null);
  const [editing, setEditing] = useState<OverrideRow | null>(null);
  const [finding, setFinding] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);
  const [engine, setEngine] = useState<EngineName>("gemini_live");
  const [profile, setProfile] = useState<string | null>(null);
  const [expiry, setExpiry] = useState<number | null>(7);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(() => {
    getOverrides()
      .then((d) => {
        setData(d);
        setLoadError(null);
      })
      .catch((e) => setLoadError(errorText(e)));
  }, []);
  useEffect(load, [load]);

  const allowed = (e: EngineName) => engines.find((x) => x.engine === e)?.allowed ?? e === "cascade";
  const builtinKey = (e: EngineName) => engines.find((x) => x.engine === e)?.builtinKey ?? `${e}-default`;
  const targetUid = found?.uid ?? editing?.uid ?? null;
  const existing = found?.override ?? editing ?? null;

  const fill = (o: { engine: EngineName; profileKey: string | null; expiresAt: string | null; note: string | null }) => {
    setEngine(o.engine);
    setProfile(o.profileKey);
    setExpiry(expiryChoice(o.expiresAt, new Date()));
    setNote(o.note ?? "");
  };

  // Bumped by every lookup and every change of who is being edited. A lookup
  // answers only if nothing changed while it was in flight, so a late answer
  // for alice can't put the override typed for bob on alice.
  const findSeq = useRef(0);
  const forget = () => {
    findSeq.current += 1;
    setFinding(false);
  };

  const find = async (): Promise<UserLookup | null> => {
    const target = parseWho(who);
    if (!target) return null;
    const seq = ++findSeq.current;
    setFinding(true);
    setFindError(null);
    setEditing(null);
    try {
      const user = await lookupUser(target);
      if (seq !== findSeq.current) return null;
      setFound(user);
      if (user.override) fill(user.override);
      return user;
    } catch (e) {
      if (seq !== findSeq.current) return null;
      setFound(null);
      setFindError(errorText(e));
      return null;
    } finally {
      if (seq === findSeq.current) setFinding(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const uid = targetUid ?? (await find())?.uid;
      if (!uid) return;
      const row = await putOverride(uid, {
        engine,
        profileKey: profile,
        expiresAt: expiresAtFor(expiry, new Date()),
        note: note.trim() || null,
      });
      setToast(
        `${row.email ?? row.uid} now gets ${ENGINE_LABEL[row.engine]} · ${row.profileKey ?? builtinKey(row.engine)} on their next call (within 30 seconds).`,
      );
      setWho("");
      setFound(null);
      setEditing(null);
      setNote("");
      load();
      await onChanged();
    } catch (e) {
      if (isForbidden(e)) onForbidden(errorText(e));
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: OverrideRow) => {
    if (!confirm(`Remove ${row.email ?? row.uid}'s override? Their next call follows the policy.`)) return;
    setError(null);
    try {
      await deleteOverride(row.uid);
      setToast(`${row.email ?? row.uid} removed. Their next call follows the policy.`);
      if (editing?.uid === row.uid) setEditing(null);
      load();
      await onChanged();
    } catch (e) {
      if (isForbidden(e)) onForbidden(errorText(e));
      setError(errorText(e));
    }
  };

  const edit = (row: OverrideRow) => {
    forget();
    setWho(row.email ?? row.uid);
    setFound(null);
    setFindError(null);
    setEditing(row);
    fill(row);
    setToast(null);
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const choices = profileChoices(engine, profiles);
  const now = new Date();

  return (
    <div>
      <Callout tone="info" badge="How it works">
        A test user always gets the engine and profile set here, ahead of the experiment and the build floor (ladder
        step 3). Overrides live in the database, so deploys don't wipe them, and every call records{" "}
        <span className="mono">engine_source: override</span>. An engine not allowed on {environment} still falls back
        to the cascade.
      </Callout>
      {toast && (
        <Callout tone="ok" badge="Done" role="status">
          {toast}
        </Callout>
      )}

      <section className="panel" style={{ marginBottom: 16 }} aria-labelledby="ve-add-h">
        <h2 id="ve-add-h" className="panel-head">
          {existing ? "Change a test user" : "Add a test user"}
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <div className="ve-fields" style={{ alignItems: "flex-end" }}>
            <div className="wide">
              <label htmlFor="ve-who">Email or uid</label>
              <div className="ve-inline">
                <input
                  id="ve-who"
                  type="search"
                  value={who}
                  placeholder="name@example.com"
                  onChange={(e) => {
                    forget();
                    setWho(e.target.value);
                    setFound(null);
                    setEditing(null);
                    setFindError(null);
                  }}
                />
                <button type="button" className="btn" disabled={!who.trim() || finding} onClick={() => find()}>
                  {finding ? "Finding…" : "Find"}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="ve-eng">Engine</label>
              <select
                id="ve-eng"
                value={engine}
                onChange={(e) => {
                  setEngine(e.target.value as EngineName);
                  setProfile(null);
                }}
              >
                {ENGINE_ORDER.map((e) => (
                  <option key={e} value={e}>
                    {ENGINE_LABEL[e]}
                    {allowed(e) ? "" : " (not allowed here)"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="ve-prof">Profile</label>
              <select
                id="ve-prof"
                className="mono"
                value={profile ?? ""}
                onChange={(e) => setProfile(e.target.value || null)}
              >
                <option value="">{builtinKey(engine)} (built-in)</option>
                {choices
                  .filter((p) => !p.builtin)
                  .map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.key} · v{p.version}
                    </option>
                  ))}
                {profile && !choices.some((p) => p.key === profile) && (
                  <option value={profile}>{profile} (archived or missing)</option>
                )}
              </select>
            </div>
            <div>
              <label htmlFor="ve-expires">Expires</label>
              <select
                id="ve-expires"
                value={expiry === null ? "never" : String(expiry)}
                onChange={(e) => setExpiry(e.target.value === "never" ? null : Number(e.target.value))}
              >
                {EXPIRY_DAYS.map((d) => (
                  <option key={String(d)} value={d === null ? "never" : String(d)}>
                    {expiryLabel(d)}
                  </option>
                ))}
              </select>
            </div>
            <div className="wide">
              <label htmlFor="ve-unote">Note</label>
              <input
                id="ve-unote"
                type="text"
                maxLength={500}
                value={note}
                placeholder="What they are testing"
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <div style={{ flex: "0 0 auto" }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!canWrite || !who.trim() || saving || finding || findError !== null}
              >
                {saving ? "Saving…" : existing ? "Update" : "Add"}
              </button>
            </div>
          </div>
        </form>
        {findError && <p className="error-text">{findError}</p>}
        {found && (
          <p className="muted ve-small" style={{ marginBottom: 0 }}>
            Found {found.email ?? found.uid}
            {found.displayName ? ` (${found.displayName})` : ""} · uid <span className="mono">{found.uid}</span> ·{" "}
            {clientText(found.client)} · last spoken call{" "}
            {found.lastCallAt ? formatDateTime(found.lastCallAt) : "none recorded"}.{" "}
            Without an override: {ENGINE_LABEL[found.withoutOverride.engine]} ·{" "}
            {profileText(found.withoutOverride.profileKey, found.withoutOverride.profileVersion)} (
            {decisionWhy(found.withoutOverride)}).
            {found.override &&
              ` Has an override now: ${ENGINE_LABEL[found.override.engine]} · ${found.override.profileKey ?? builtinKey(found.override.engine)}, ${expiryText(found.override.expiresAt, now).text}.`}
            {found.disabled && <strong style={{ color: "var(--warn)" }}> The account is disabled.</strong>}
          </p>
        )}
        {editing && !found && (
          <p className="muted ve-small" style={{ marginBottom: 0 }}>
            Editing the override of uid <span className="mono">{editing.uid}</span>.
          </p>
        )}
        {error && <p className="error-text">{error}</p>}
      </section>

      <section className="panel" aria-labelledby="ve-users-h">
        <h2 id="ve-users-h" className="panel-head">
          Test users{data ? ` · ${data.active} active` : ""}
        </h2>
        {loadError ? (
          <p className="error-text">Test users unavailable ({loadError}).</p>
        ) : !data ? (
          <p className="muted ve-small">Loading…</p>
        ) : data.overrides.length === 0 ? (
          <p className="muted ve-small">No test users yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="guide-table ve-users" aria-label="Test users">
              <thead>
                <tr>
                  <th className="no-sort">User</th>
                  <th className="no-sort">Override</th>
                  <th className="no-sort">Expires</th>
                  <th className="no-sort">Note</th>
                  <th className="no-sort">Their next call</th>
                  <th className="no-sort">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.overrides.map((r) => {
                  const exp = expiryText(r.expiresAt, now);
                  const next = r.nextCall;
                  const tone = !r.active
                    ? "var(--muted)"
                    : next.reason
                      ? "var(--warn)"
                      : next.source === "override"
                        ? "var(--accent-2)"
                        : "var(--muted)";
                  return (
                    <tr key={r.uid}>
                      <td className={r.active ? "" : "ve-faded"}>
                        <div>{r.email ?? "no email found"}</div>
                        <div className="muted ve-small">
                          uid <span className="mono">{r.uid}</span> · {clientText(r.client)}
                        </div>
                      </td>
                      <td className={r.active ? "" : "ve-faded"}>
                        <div>{ENGINE_LABEL[r.engine]}</div>
                        <div className="mono muted ve-small">{r.profileKey ?? `${builtinKey(r.engine)} (built-in)`}</div>
                      </td>
                      <td>
                        <span className={`badge ${TONE_CLASS[exp.tone]}`}>{exp.text}</span>
                      </td>
                      <td className={`ve-small${r.active ? "" : " ve-faded"}`}>
                        {r.note || "—"}
                        <div className="muted">
                          {r.createdBy ?? "unknown"} · {formatDateTime(r.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {ENGINE_LABEL[next.engine]}{" "}
                          <span className="mono muted" style={{ fontWeight: 400 }}>
                            {profileText(next.profileKey, next.profileVersion)}
                          </span>
                        </div>
                        <div className="ve-small" style={{ color: tone }}>
                          {r.active ? decisionWhy(next) : `expired, back to the policy: ${decisionWhy(next)}`}
                        </div>
                      </td>
                      <td>
                        <div className="row-actions">
                          <button type="button" className="btn btn-sm" disabled={!canWrite} onClick={() => edit(r)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            disabled={!canWrite}
                            onClick={() => remove(r)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {data && (
        <section className="panel ve-section" aria-labelledby="ve-env-h">
          <h2 id="ve-env-h" className="panel-head">
            Env fallback
          </h2>
          <div className="ve-row">
            <span className="mono">VOICE_ENGINE_OVERRIDE_UIDS</span>
            <span className="chip">
              {data.env.uids.length} uid{data.env.uids.length === 1 ? "" : "s"}
            </span>
            <span className="muted ve-small">
              → <span className="mono">VOICE_S2S_DEFAULT_ENGINE</span> ({data.env.defaultEngine})
            </span>
            <span className="badge ve-warn">deprecated</span>
          </div>
          <div className="ve-help">
            Still honoured below the overrides above (ladder step 4), and changed only by a deploy.
            {data.env.uids.length > 0 && " Move these to Studio, then drop the env list."}
          </div>
          {data.env.uids.length > 0 && (
            <details className="ve-details" style={{ marginTop: 6 }}>
              <summary>Show uids</summary>
              <div className="chip-row" style={{ marginTop: 6 }}>
                {data.env.uids.map((u) => (
                  <span key={u} className="chip mono">
                    {u}
                  </span>
                ))}
              </div>
            </details>
          )}
        </section>
      )}
    </div>
  );
}
